import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {usePathname} from '@aboutyou/router';
import {AdjustTracker} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  FONT_SIZES,
  Flyout,
  FlyoutBody,
  GenericIconName,
  Icon,
  Text,
} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {useGetStoreLink} from '../../StoreBadges/utils/useGetStoreLink';

const APP_ONLY_COUPON_INFO_POPPER_KEY = 'APP_ONLY_COUPON_INFO_POPPER_KEY';

export const useAppOnlyInfoFlyoutStateManager = () => {
  return useOpenedStateManager(APP_ONLY_COUPON_INFO_POPPER_KEY);
};

export const AppOnlyInfoFlyout: React.FC<{
  'data-testid'?: string;
}> = ({'data-testid': dataTestId}) => {
  const {isOpen, cancel} = useAppOnlyInfoFlyoutStateManager();
  const {t} = useTranslation();
  const headline = t('App only coupon');
  const body = t(
    'The coupon can be used only in our app. Download the app to activate it.',
  );

  return (
    <StyledFlyout
      data-testid={`${dataTestId}-appOnlyFlyout`}
      open={isOpen}
      onClose={cancel}
      renderInPortal
      bodyContent={
        <Wrapper>
          <Content>
            <HeaderWrapper>
              <AppOnlyCouponIcon
                data-testid={`${dataTestId}-appOnlyIcon`}
                asset={GenericIconName.AppOnlyCoupon}
                size="XXL"
              />
              <StyledText
                size="px16"
                weight="bold"
                data-testid={`${dataTestId}-headline`}
              >
                {headline}
              </StyledText>
            </HeaderWrapper>
            <BodyText
              size="px14"
              weight="mediumBold"
              data-testid={`${dataTestId}-bodyText`}
            >
              {body}
            </BodyText>
          </Content>
          <Footer>
            <DownloadButton />
          </Footer>
        </Wrapper>
      }
    />
  );
};

export const DownloadButton: React.FC<{className?: string}> = ({className}) => {
  const {t} = useTranslation();

  const pathname = usePathname();
  // TODO: update the values below with ticket https://aboutyou.atlassian.net/browse/ASFE-10335
  const adjustTracker = AdjustTracker.DESK_OSP_WEB_COUPON_BANNER;
  const CTALink = useGetStoreLink({
    adjustTracker,
    deepLink: pathname,
  });

  return (
    <StyledButton
      className={className}
      href={CTALink}
      onClick={e => e.stopPropagation()}
      aria-label={undefined}
    >
      <Icon asset={GenericIconName.Download} fill={COLORS.White} size="SS" />
      {t('Download App')}
    </StyledButton>
  );
};

const StyledFlyout = styled(Flyout)`
  ${FlyoutBody} {
    padding: 20px 0 0 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 8px;
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  overflow-wrap: anywhere;
  padding: 0 16px 16px;
  text-align: center;
`;

const Footer = styled.div`
  align-items: center;
  border-top: 1px solid ${COLORS.Grey3};
  display: flex;
  gap: 4px;
  justify-content: center;
  padding: 16px;
  width: 100%;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-content: center;
  flex-wrap: wrap;
  justify-content: center;
  padding-inline: 16px;
  padding-top: 0;
  gap: 8px;
`;

const AppOnlyCouponIcon = styled(Icon)`
  margin: auto;
`;

const StyledText = styled(Text)`
  line-height: ${FONT_SIZES.px20};
`;

const BodyText = styled(StyledText)`
  display: block;
  padding-inline: 16px;
`;

const StyledButton = styled(Button)`
  &&&& {
    --background-color: ${COLORS['Coupon.Primary']};
    min-height: auto;
  }
`;
